import React, { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import parse from 'html-react-parser';
import images from '_images';
import { _ } from '_locales';

import HOC from '_HOC';
import { changeLanguage, convertLanguage } from '_helpers';
import { colors as fixedColors } from '_styles';
import { config as configSetup } from '_actions';

// import svg components
import NotificationIcon from './Images/WebappHeader/NotificationIcon';
import Tabs from './Tabs';

const parentWidth = document.getElementById('rcroot').getBoundingClientRect().width;

const Container = styled.nav`
  width: 100%;
  height: auto;
  overflow: hidden;
  position: relative;
  border-radius: 5px 5px 0 0;
  background: ${({ primaryColor }) => (primaryColor ? `#${primaryColor}` : fixedColors.RC_ROYAL)};
  ${({ primaryColor, secondaryColor }) => (primaryColor && secondaryColor) && css`
    background: linear-gradient(90deg, #${primaryColor} 0%, #${secondaryColor} 35%);
  `};
  box-sizing: border-box;
  border-right: 1px solid ${({ primaryColor }) => (primaryColor ? `#${primaryColor}` : fixedColors.RC_ROYAL)};
  border-left: 1px solid ${({ primaryColor }) => (primaryColor ? `#${primaryColor}` : fixedColors.RC_ROYAL)};
`;

const TopHeaderContainer = styled.div`
  padding: 12px 15px;
  display: flex;
  flex-direction: ${({ mobileBreakPoint }) => (mobileBreakPoint ? 'column' : 'row')};
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  height: ${({ hasMoreThanOneFeature }) => (hasMoreThanOneFeature ? 'calc(100% - 42px)' : 'calc(100% - -2px)')};
`;

const Text = styled.h1`
  color: #fff !important;
  font-size: 18px !important;
  font-weight: 500 !important;
  text-align: center;
  /* white-space: nowrap */
`;

const Controls = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  justify-content: center;
`;

const HelpButton = styled.button`
  font-family: Roboto, sans-serif !important;
  color: ${({ disabled }) => ((disabled) ? '#C2C2C2' : '#fff')} !important;
  border: 1px solid ${({ disabled }) => ((disabled) ? '#C2C2C2' : '#fff')} !important;
  border-radius: 8px !important;
  background-color: transparent !important;
  padding: 4px 13px !important;
  box-sizing: border-box;
  cursor: pointer;
  margin: 0 !important;
  font-size: 15px !important;
  font-weight: 400 !important;
  min-width: 145px;
`;

const LanguageSelect = styled.select`
  border: none;
  background: transparent;
  padding: 0px 13px 0px 0px;
  box-sizing: border-box;
  color: white;
  width: 70px;
  max-width: 70px;
  margin-left: 10px;
  cursor: pointer;

  appearance: none;
  background-image: url(${images.selectArrowDown});
  background-repeat: no-repeat;
  background-color: transparent;
  background-size: 17px;
  background-position: right center;

  font-size: 14px !important;
   
  font-weight: 400 !important;
  option {
    color: black;
  }
`;

/**
 * This component is for making the header and also check the multi language feature
 *
 * @author Mahziar Eghdami
 * @param {object} config
 * @param {function} setSelectedLang
 */

const WebappHeader = ({
  zone,
  config,
  help,
  colors,
}) => {
  const titleRef = useRef();
  const controlsRef = useRef();
  const containerRef = useRef();
  const { main_color, second_color } = colors;

  const {
    langs,
    title,
    districtId,
    defaultLang,
    selectedLang,
    webappConfig,
  } = config;

  const {
    has_news = false,
    has_wgw = false,
    has_help = false,
    help_key_text = '',
    has_calendar = false,
    has_dz = false,
    has_bulk_pickup = false
  } = webappConfig;

  const { zoneStr } = zone;
  const { helpItems } = help;
  const hasMoreThanOneFeature = (has_calendar + has_wgw + has_dz + has_bulk_pickup) > 1;

  const [isLangListDisabled, setIsLangListDisabled] = useState(false);
  const [titleWidth, setTitleWidth] = useState();
  const [controlsWidth, setControlsWidth] = useState();
  const [containerWidth, setContainerWidth] = useState();
  const history = useHistory();
  const mobileBreakPoint = containerWidth - titleWidth - controlsWidth < 49;

  const changeHandler = async ({ target }) => {
    try {
      setIsLangListDisabled(true);
      await changeLanguage(target.value);
      setIsLangListDisabled(false);
      return undefined;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  useEffect(() => {
    const updateContainerDimensions = () => setContainerWidth(containerRef.current.clientWidth);
    window.addEventListener('resize', updateContainerDimensions);
    return () => window.removeEventListener('resize', updateContainerDimensions);
  }, []);

  useEffect(() => {
    if (titleRef.current && controlsRef.current && containerRef.current) {
      setControlsWidth(controlsRef.current.clientWidth);
      setTitleWidth(titleRef.current.clientWidth);
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    // change html lang tag conditionally
    const converted = convertLanguage(selectedLang);
    document.documentElement.lang = converted || 'en';
  }, [selectedLang]);

  const navigate = (path, params = {}) => {
    if (!path) {
      if (process.env.NODE_ENV !== 'production') { console.warn('Path not provided to navigate'); }
      return;
    }

    if (history.location.pathname === path) return;

    if (path === '/cal' && !zoneStr) {
      history.push('/zone-setup');
    } else {
      history.push(path, params);
    }
  };

  return (
    <Container
      primaryColor={main_color}
      secondaryColor={second_color}
    >
      <TopHeaderContainer
        key={mobileBreakPoint}
        ref={containerRef}
        mobileBreakPoint={mobileBreakPoint}
        hasMoreThanOneFeature={hasMoreThanOneFeature}
      >
        <Text ref={titleRef}>{parse(title)}</Text>

        <Controls ref={controlsRef}>
          {has_news && <NotificationIcon onClick={() => navigate('/news')} />}

          {has_help && helpItems.length > 0 && (
            <HelpButton
              onClick={() => navigate('/help')}
              aria-label="open the help page"
              disabled={((!has_calendar && !districtId) || false)}
            >
              {(has_help && help_key_text.length) ? help_key_text : _('general.help')}
            </HelpButton>
          )}

          {langs && Object.keys(langs).length > 1 ? (
            <LanguageSelect
              id="langs-select"
              disabled={isLangListDisabled}
              aria-label="select one of the languages"
              aria-labelledby="select one of the languages"
              value={selectedLang || defaultLang}
              onChange={changeHandler}
            >
              {Object.values(langs).map((lang) => (
                <option key={lang.lang_cd} value={lang.lang_cd}>
                  {lang.label}
                </option>
              ))}
            </LanguageSelect>
          ) : null}

        </Controls>
      </TopHeaderContainer>

      {districtId && hasMoreThanOneFeature
        && <Tabs navigate={navigate} />}

    </Container>
  );
};

const mapStateToProps = (store) => ({
  colors: store.config.templateColour ? JSON.parse(store.config.templateColour) : {},
});

const mapDispatchToProps = (dispatch) => ({
  setCollection: (collection) => dispatch(configSetup.setCollection(collection)),
  setSelectedLang: (selectedLang) => dispatch(configSetup.setSelectedLang(selectedLang)),
});

export default HOC(connect(mapStateToProps, mapDispatchToProps)(WebappHeader));
